import { useCallback, useRef, useState } from "react";
import { create } from "zustand";
import {
  DEFAULT_ROOM_RAIL_PREFERENCES,
  parseRoomRailPreferences,
  roomRailScope,
  type RoomRailPreferences,
} from "./roomRailArrangement";
import { useNeucleosWorldStore } from "./worldStore";
import { toastManager } from "~/components/ui/toast";

export const ROOM_RAIL_PREFERENCES_KEY = "cleo:room-arrangement:v1";
export function readRoomRailPreferences(
  storage: Pick<Storage, "getItem"> | undefined,
): Record<string, RoomRailPreferences> {
  try {
    const raw: unknown = JSON.parse(storage?.getItem(ROOM_RAIL_PREFERENCES_KEY) ?? "{}");
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
    return Object.fromEntries(
      Object.entries(raw).map(([scope, value]) => [scope, parseRoomRailPreferences(value)]),
    );
  } catch {
    return {};
  }
}
const initial = () => {
  try {
    return readRoomRailPreferences(window.localStorage);
  } catch {
    return {};
  }
};
export const useRoomRailPreferencesStore = create<{ buckets: Record<string, RoomRailPreferences> }>(
  () => ({ buckets: initial() }),
);

export function saveRoomRailPreferences(
  scope: string,
  prefs: RoomRailPreferences,
  storage?: Pick<Storage, "getItem" | "setItem">,
): boolean {
  let saved = false;
  let buckets = useRoomRailPreferencesStore.getState().buckets;
  try {
    const target = storage ?? window.localStorage;
    // Preserve another tab's independent World/Actor buckets.
    buckets = {
      ...buckets,
      ...readRoomRailPreferences(target),
      [scope]: parseRoomRailPreferences(prefs),
    };
    target.setItem(ROOM_RAIL_PREFERENCES_KEY, JSON.stringify(buckets));
    saved = true;
  } catch {
    buckets = { ...buckets, [scope]: parseRoomRailPreferences(prefs) };
  }
  useRoomRailPreferencesStore.setState({ buckets });
  return saved;
}
if (typeof window !== "undefined")
  window.addEventListener("storage", (event) => {
    if (event.key === ROOM_RAIL_PREFERENCES_KEY)
      useRoomRailPreferencesStore.setState({ buckets: initial() });
  });
export function useRoomRailPreferences(worldKey: string) {
  const scope = useNeucleosWorldStore((state) =>
    roomRailScope(
      state.signedIn ? state.identity : null,
      state.worlds.find((world) => world.worldKey === worldKey)?.actorId,
      worldKey,
    ),
  );
  const [transient, setTransient] = useState({
    worldKey,
    preferences: DEFAULT_ROOM_RAIL_PREFERENCES,
  });
  const stored = useRoomRailPreferencesStore((state) =>
    scope === null
      ? DEFAULT_ROOM_RAIL_PREFERENCES
      : (state.buckets[scope] ?? DEFAULT_ROOM_RAIL_PREFERENCES),
  );
  const preferences =
    scope === null
      ? transient.worldKey === worldKey
        ? transient.preferences
        : DEFAULT_ROOM_RAIL_PREFERENCES
      : stored;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const worldKeyRef = useRef(worldKey);
  worldKeyRef.current = worldKey;
  const preferencesRef = useRef(preferences);
  preferencesRef.current = preferences;
  const revisionRef = useRef(0);
  const save = useCallback(
    (next: RoomRailPreferences) => {
      if (scope === null) {
        setTransient({ worldKey, preferences: next });
        return false;
      }
      const current = useNeucleosWorldStore.getState();
      if (
        roomRailScope(
          current.signedIn ? current.identity : null,
          current.worlds.find((world) => world.worldKey === worldKey)?.actorId,
          worldKey,
        ) !== scope
      )
        return false;
      return saveRoomRailPreferences(scope, next);
    },
    [scope, worldKey],
  );
  const commit = useCallback(
    (
      next: RoomRailPreferences,
      title = "Arrangement updated",
      fields?: readonly (keyof RoomRailPreferences)[],
      options?: {
        /** A saved change passes without a toast; an unsaved one still warns. */
        readonly quiet?: boolean;
      },
    ) => {
      if (scopeRef.current !== scope || worldKeyRef.current !== worldKey) return;
      const previous =
        scope === null
          ? preferencesRef.current
          : (useRoomRailPreferencesStore.getState().buckets[scope] ??
            DEFAULT_ROOM_RAIL_PREFERENCES);
      const revision = ++revisionRef.current;
      const saved = save(next);
      if (saved && options?.quiet === true) return;
      const toastId = toastManager.add({
        timeout: 10000,
        type: saved ? "success" : "warning",
        title: saved
          ? title
          : scope === null
            ? "Sign in to save your arrangement"
            : "Arrangement kept for this visit",
        description:
          saved || scope === null
            ? undefined
            : "Device storage is unavailable. Your changes will not survive a reload.",
        actionProps:
          saved || fields !== undefined
            ? {
                children: "Undo",
                onClick: () => {
                  const latest =
                    scope === null
                      ? preferencesRef.current
                      : (useRoomRailPreferencesStore.getState().buckets[scope] ??
                        DEFAULT_ROOM_RAIL_PREFERENCES);
                  if (
                    revisionRef.current !== revision ||
                    scopeRef.current !== scope ||
                    worldKeyRef.current !== worldKey ||
                    (fields
                      ? fields.some(
                          (field) => JSON.stringify(latest[field]) !== JSON.stringify(next[field]),
                        )
                      : JSON.stringify(useRoomRailPreferencesStore.getState().buckets[scope!]) !==
                        JSON.stringify(next))
                  )
                    return;
                  save(
                    fields
                      ? {
                          ...latest,
                          ...Object.fromEntries(fields.map((field) => [field, previous[field]])),
                        }
                      : previous,
                  );
                  revisionRef.current++;
                  toastManager.close(toastId);
                },
              }
            : undefined,
      });
    },
    [save, scope, worldKey],
  );
  const togglePin = useCallback(
    (sessionId: string) => {
      const current =
        scope === null
          ? preferencesRef.current
          : (useRoomRailPreferencesStore.getState().buckets[scope] ??
            DEFAULT_ROOM_RAIL_PREFERENCES);
      const pinned = current.pinnedSessionIds.includes(sessionId);
      commit(
        {
          ...current,
          pinnedSessionIds: pinned
            ? current.pinnedSessionIds.filter((id) => id !== sessionId)
            : [...current.pinnedSessionIds, sessionId],
        },
        pinned ? "Session unpinned" : "Session pinned",
      );
    },
    [commit, scope],
  );
  return { preferences, save, scope, commit, togglePin };
}
